// The share table and its confirmation (solution sharing plan, stage 7, O20-O22). Pure helpers, used by
// the Solution Edit pop-up and the creation wizard's last screen.

const idOf = (x) => String(x && x._id !== undefined ? x._id : x);
const byName = (a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });

// Offered rows first (tickable), then the rows a list rule excludes (greyed; grant-all callers only),
// each sorted by name ignoring case.
export function tableRows(targets, notOffered) {
  return {
    offered: [...(targets || [])].sort(byName),
    greyed: [...(notOffered || [])].sort(byName),
  };
}

// Case-insensitive part of the name; an empty search shows every row.
export function filterRows(rows, search) {
  const q = String(search || "").trim().toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((r) => String(r.name || "").toLowerCase().includes(q));
}

// The header checkbox over the tickable rows the search shows: "disabled" when none is shown,
// "checked" when all are ticked, "partial" when some are, "clear" when none.
export function headerState(shownTickable, selected) {
  const ids = (shownTickable || []).map(idOf);
  if (!ids.length) return "disabled";
  const sel = new Set((selected || []).map(String));
  const n = ids.filter((id) => sel.has(id)).length;
  return n === ids.length ? "checked" : n ? "partial" : "clear";
}

// A click on the header checkbox: when checked it unticks the shown tickable rows, otherwise it ticks
// them. Rows the search hides never change.
export function toggleHeader(shownTickable, selected) {
  const ids = (shownTickable || []).map(idOf);
  const sel = new Set((selected || []).map(String));
  if (headerState(shownTickable, selected) === "checked") ids.forEach((id) => sel.delete(id));
  else ids.forEach((id) => sel.add(id));
  return [...sel];
}

// The selection line: n = every ticked row; hidden = ticked rows the search hides.
export function selectionLine(selected, shownIds) {
  const shown = new Set((shownIds || []).map(String));
  const sel = (selected || []).map(String);
  return { n: sel.length, hidden: sel.filter((id) => !shown.has(id)).length };
}

// The confirmation's headline numbers for the chosen rows: N chosen roles, M administrator roles that
// also receive it (each once, chosen ones not counted again), U users over all of them (null when a
// count is missing, e.g. an older rolesMS), K chosen roles that have not opted in (grant-all only).
export function confirmationTotals(targets, chosenIds, grantAll) {
  const chosenSet = new Set((chosenIds || []).map(String));
  const chosen = (targets || []).filter((t) => chosenSet.has(idOf(t)));
  const admins = new Map();
  for (const t of chosen) for (const a of t.alsoTo || []) if (!chosenSet.has(idOf(a))) admins.set(idOf(a), a);
  const all = [...chosen, ...admins.values()];
  const users = all.every((r) => typeof r.users === "number") ? all.reduce((s, r) => s + r.users, 0) : null;
  const notOptedIn = grantAll ? chosen.filter((t) => t.receives === false).length : 0;
  return { chosen: chosen.length, administrators: admins.size, users, notOptedIn };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export function confirmationHeadline(totals, solutionName) {
  const users = totals.users === null ? "" : ` (${plural(totals.users, "user", "users")})`;
  const admins = totals.administrators ? ` and ${plural(totals.administrators, "administrator role", "administrator roles")}` : "";
  return `${plural(totals.chosen, "chosen role", "chosen roles")}${admins}${users} will get ${solutionName || "this solution"}`;
}
export function notOptedInLine(totals) {
  if (!totals.notOptedIn) return "";
  return totals.notOptedIn === 1
    ? "1 of the chosen roles has not opted in to receive shared solutions."
    : `${totals.notOptedIn} of the chosen roles have not opted in to receive shared solutions.`;
}

// The share's answer was lost (a network error, or a 502/503/504 without a JSON body).
export function isLostAnswer(error) {
  if (!error) return false;
  if (error.status === "FETCH_ERROR" || error.status === "TIMEOUT_ERROR") return true;
  if ([502, 503, 504].includes(error.status)) return !error.data || typeof error.data !== "object";
  return error.status === "PARSING_ERROR" && [502, 503, 504].includes(error.originalStatus);
}

// After a lost answer, from a reload of share-targets: a chosen role missing from both lists now holds
// the solution. A grant-all caller sees every role lacking it, so that is certain ("shared"); a finite
// creator sees only Receive-on roles, so such a role may also have switched Receive off
// ("probably-shared"). A role still listed was not shared and can be retried (RETRY: the reload proves it
// still lacks the solution, and the server answers CONFIRM_AGAIN if its lists grew).
export function lostAnswerReport(chosen, reload, grantAll) {
  const listed = new Set([...((reload && reload.payload) || []), ...((reload && reload.notOffered) || [])].map(idOf));
  return (chosen || []).map((t) => {
    const id = idOf(t);
    if (listed.has(id)) return { roleId: id, roleName: t.name, status: "skipped", errorCode: "RETRY", reason: "the answer was lost; it can be shared again", written: [] };
    return grantAll
      ? { roleId: id, roleName: t.name, status: "shared", written: [] }
      : { roleId: id, roleName: t.name, status: "probably-shared", reason: "no longer offered", written: [] };
  });
}

// The results' headline: "<n> shared[, <p> probably shared], <m> not shared".
export function resultsHeadline(results) {
  const n = (results || []).filter((r) => r.status === "shared").length;
  const p = (results || []).filter((r) => r.status === "probably-shared").length;
  const m = (results || []).length - n - p;
  return `${n} shared${p ? `, ${p} probably shared` : ""}, ${m} not shared`;
}

// The administrator column: two names, then "+k more".
export function shortList(list, max = 2) {
  const names = (list || []).map((x) => x.name);
  return { shown: names.slice(0, max), more: Math.max(0, names.length - max), all: names };
}
